import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API_BASE_URL } from '../../utils/api';
import { toast } from 'react-toastify';
import { Eye, EyeOff } from 'lucide-react';

const Register = () => {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [otp, setOtp] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showOtpModal, setShowOtpModal] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(30);

  const navigate = useNavigate();

  useEffect(() => {
    let timer;
    if (showOtpModal && resendCooldown > 0) {
      timer = setTimeout(() => setResendCooldown((prev) => prev - 1), 1000);
    }
    return () => clearTimeout(timer);
  }, [showOtpModal, resendCooldown]);

  const handleSignup = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await axios.post(`${API_BASE_URL}/api/auth/send-otp`, {
        name,
        email,
        password,
      });
      toast.success('OTP sent to your email');
      setShowOtpModal(true);
      setResendCooldown(30);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to send OTP');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOtp = async () => {
    if (otp.length !== 6) {
      return toast.error('OTP must be 6 digits');
    }

    try {
      await axios.post(`${API_BASE_URL}/api/auth/verify-otp`, {
        email,
        otp,
      });
      toast.success('Signup successful');
      setShowOtpModal(false);
      navigate('/login');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Invalid OTP');
    }
  };

  const handleResendOtp = async () => {
    try {
      await axios.post(`${API_BASE_URL}/api/auth/send-otp`, {
        name,
        email,
        password,
      });
      toast.success('OTP resent to your email');
      setResendCooldown(30);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to resend OTP');
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#0E0A1F] text-white px-4 font-sans">
      <div className="bg-[#1C1235] text-white p-8 rounded-xl shadow-md w-full max-w-md">
        <div className="flex justify-center mb-6">
          <img src="/Spark_01.png" alt="Sparks Logo" className="h-12" />
        </div>

        <h2 className="text-2xl font-bold font-poppins text-white mb-1 text-center">
          Create an Account
        </h2>
        <p className="text-sm text-white text-center mb-6 font-light">
          Join Sparks Scribe to manage clients and shifts with ease.
        </p>

        <form onSubmit={handleSignup} className="space-y-4">
          <div>
            <label htmlFor="name" className="text-sm block mb-1 font-light text-white">Full Name</label>
            <input
              id="name"
              type="text"
              className="w-full p-3 border border-[#6D5BAF] rounded bg-white text-[#34304D]"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
            />
          </div>

          <div>
            <label htmlFor="email" className="text-sm block mb-1 font-light text-white">Email</label>
            <input
              id="email"
              type="email"
              className="w-full p-3 border border-[#6D5BAF] rounded bg-white text-[#34304D]"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
          </div>

          <div className="relative">
            <label htmlFor="password" className="text-sm block mb-1 font-light text-white">Password</label>
            <input
              id="password"
              type={showPassword ? 'text' : 'password'}
              className="w-full p-3 pr-10 border border-[#6D5BAF] rounded bg-white text-[#34304D]"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
            />
            <div
              className="absolute top-10 right-3 cursor-pointer"
              onClick={() => setShowPassword((prev) => !prev)}
            >
              {showPassword ? (
                <EyeOff className="text-[#34304D] w-5 h-5" />
              ) : (
                <Eye className="text-[#34304D] w-5 h-5" />
              )}
            </div>
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full py-3 rounded text-white font-semibold bg-[#6D5BAF] hover:bg-[#5a4aa5] transition"
          >
            {loading ? 'Signing up...' : 'Sign Up'}
          </button>
        </form>

        <p className="text-sm text-center mt-4 text-white font-light">
          Already have an account?{' '}
          <span
            onClick={() => navigate('/login')}
            className="cursor-pointer font-medium text-[#6D5BAF] hover:underline"
          >
            Log in
          </span>
        </p>
      </div>

      {showOtpModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="relative bg-white text-black p-6 rounded-xl w-full max-w-sm">
            {/* Close Button */}
            <button
              onClick={() => setShowOtpModal(false)}
              className="absolute top-2 right-2 text-gray-500 hover:text-black text-2xl font-bold"
            >
              ×
            </button>

            <h3 className="text-lg font-semibold mb-4 text-center">Verify OTP</h3>

            <input
              type="text"
              value={otp}
              onChange={(e) => setOtp(e.target.value)}
              className="w-full p-3 border rounded mb-4"
              placeholder="Enter 6-digit OTP"
            />

            <button
              onClick={handleVerifyOtp}
              className="w-full py-2 bg-[#6D5BAF] text-white rounded hover:bg-[#5a4aa5] mb-2"
            >
              Verify & Complete Signup
            </button>

            <button
              onClick={handleResendOtp}
              disabled={resendCooldown > 0}
              className="w-full py-2 text-sm rounded text-[#6D5BAF] disabled:text-gray-400 disabled:cursor-not-allowed"
            >
              {resendCooldown > 0
                ? `Resend OTP in ${resendCooldown}s`
                : 'Resend OTP'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default Register;
